import { Button } from "@/components/ui/button";
import { AvatarIniciales, Icon, Tip } from "@/components/ds";
import { formatRut } from "@/lib/rut";
import type { Trabajador } from "@/types/trabajador";

function formatFechaCorta(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export interface PapeleraTrabajadoresProps {
  trabajadores: Trabajador[];
  onRestaurar: (t: Trabajador) => void;
  onEliminar: (t: Trabajador) => void;
}

/**
 * Papelera del listado de Trabajadores.
 *
 * Es una VISTA HERMANA de la tabla, no un filtro suyo: un trabajador en la
 * papelera no tiene documentación al día ni capacitaciones pendientes que
 * mostrar — salió de la dotación. Por eso no reusa `DataTable` con las mismas
 * columnas, sino una lista corta con las dos únicas acciones que tienen
 * sentido acá.
 */
export function PapeleraTrabajadores({
  trabajadores,
  onRestaurar,
  onEliminar,
}: PapeleraTrabajadoresProps) {
  return (
    <div className="overflow-hidden rounded-2xl border">
      <div className="border-border-subtle flex items-center gap-2.5 border-b px-4 py-3">
        <Icon name="delete" size={17} className="text-icon-soft" />
        <span className="flex-1 text-[13.5px] font-semibold tracking-[-.01em]">Papelera</span>
        <span className="text-soft text-[11.5px]">
          {trabajadores.length === 1
            ? "1 trabajador en la papelera"
            : `${trabajadores.length} trabajadores en la papelera`}
        </span>
      </div>

      {trabajadores.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
          <Icon name="delete" size={28} className="text-icon-soft" />
          <span className="text-muted-foreground text-[13.5px]">La papelera está vacía.</span>
          <span className="text-soft max-w-[440px] text-[12px] leading-[1.5]">
            Cuando mueves a un trabajador a la papelera deja de aparecer en la dotación, pero
            conserva sus documentos y su historial hasta que lo elimines definitivamente.
          </span>
        </div>
      ) : (
        trabajadores.map((t) => {
          const desde = formatFechaCorta(t.eliminado_at);
          return (
            <div
              key={t.id}
              className="border-border-faint flex flex-wrap items-center gap-3 border-b px-4 py-2.5 last:border-b-0"
            >
              {/* Gris y no teal: el DS reserva el tono apagado para lo que está
                  fuera de circulación, y la papelera es exactamente eso. */}
              <AvatarIniciales iniciales={t.iniciales} tamano="listado" tono="neutro" />
              <div className="flex min-w-[160px] flex-1 flex-col gap-px">
                <span className="text-muted-foreground text-[13px] font-medium">
                  {t.nombre} {t.apellido}
                </span>
                <span className="text-soft text-[11px]">
                  <span className="fc-num">{formatRut(t.rut)}</span>
                  {t.cargo?.nombre ? ` · ${t.cargo.nombre}` : ""}
                  {desde ? ` · en papelera desde ${desde}` : ""}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="xs" onClick={() => onRestaurar(t)}>
                  <Icon name="restore_from_trash" size={16} />
                  Restaurar
                </Button>
                <Tip texto="Eliminar definitivamente">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive hover:text-destructive"
                    aria-label={`Eliminar definitivamente a ${t.nombre} ${t.apellido}`}
                    onClick={() => onEliminar(t)}
                  >
                    <Icon name="delete_forever" size={16} />
                  </Button>
                </Tip>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
